import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { fetchExtension } from 'flow/actions/extension-actions';
import {
    fetchExtensionLicenseDescriptions,
    fetchPbxLicense,
} from 'flow/actions/license-actions';
import { fetchSocialIntegrationSettings } from 'flow/actions/social-integration-actions';
import AboutListItem from 'flow/components/Setting/Account/AboutListItem';
import SocialIntegration from 'flow/components/Setting/Account/SocialIntegration';
import Page from 'flow/components/Setting/Page';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import Divider from 'flow/components/ui-components/Divider';
import Loading from 'flow/components/ui-components/Loading';
import RouteLink from 'flow/components/ui-components/RouteLink';
import useAppContext from 'flow/hooks/useAppContext';
import {
    selectAutoLogin,
    selectIsSinglePaneMode,
} from 'flow/selectors/app-selectors';
import { selectCustomer } from 'flow/selectors/customer-selectors';
import {
    selectContact,
    selectExtensionStatus,
    selectHasGoogleContactOrCalendarCapability,
    selectHasMicrosoftContactOrCalendarCapability,
} from 'flow/selectors/ExtensionSelectors';
import {
    selectExtensionLicenseDescription,
    selectPbxLicenseDescription,
} from 'flow/selectors/license-selectors';
import { selectSocialIntegrationSettings } from 'flow/selectors/social-integration-selectors';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { extractName } from 'flow/utils';
import { isTeamsAnyVersion } from 'flow/utils/app-utils';
import { isBundleCustomer } from 'flow/utils/bundle-utils';
import { retailerKeysDisallowingConnectedServices } from 'flow/utils/retailer-utils';
import { generatePath } from 'flow/utils/router-utils';

const Account = () => {
    const { i18next, embedded } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();

    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();

    const autoLoginEnabled = useAppSelector(selectAutoLogin);
    const contact = useAppSelector((state) =>
        selectContact(state, extensionKey),
    );
    const customer = useAppSelector((state) =>
        selectCustomer(state, extensionKey),
    );
    const extensionLicenseDescription = useAppSelector((state) =>
        selectExtensionLicenseDescription(state, extensionKey),
    );
    const extensionStatus = useAppSelector((state) =>
        selectExtensionStatus(state, extensionKey),
    );
    const hasMicrosoftContactOrCalendarCapability = useAppSelector((state) =>
        selectHasMicrosoftContactOrCalendarCapability(state, extensionKey),
    );
    const hasGoogleContactOrCalendarCapability = useAppSelector((state) =>
        selectHasGoogleContactOrCalendarCapability(state, extensionKey),
    );
    const isSinglePaneMode = useAppSelector(selectIsSinglePaneMode);
    const pbxLicenseDescription = useAppSelector(selectPbxLicenseDescription);
    const socialIntegrationSettings = useAppSelector(
        selectSocialIntegrationSettings,
    );

    useEffect(() => {
        dispatch(fetchExtensionLicenseDescriptions());
        dispatch(fetchSocialIntegrationSettings());
        dispatch(fetchPbxLicense());
        dispatch(fetchExtension(extensionKey, 'settings'));
    }, []);

    if (!customer) {
        return <Loading />;
    }

    const retailerAllowsConnectedServices =
        !retailerKeysDisallowingConnectedServices.includes(
            customer.getIn(['retailer', 'key']),
        );

    const getLicenceText = () =>
        [pbxLicenseDescription, extensionLicenseDescription]
            .filter(Boolean)
            .join(' / ');

    return (
        <Page
            header="Settings.Account"
            onClickBack={
                isSinglePaneMode
                    ? () =>
                          history.push(
                              generatePath(routes['settings'], {
                                  extensionKey,
                              }),
                          )
                    : undefined
            }
        >
            <div className="flex items-center gap-x-6">
                <ContactAvatar
                    contact={contact}
                    status={extensionStatus}
                    size="large"
                />
                <div className="text-2xl whitespace-nowrap">
                    {extractName(contact, i18next.t('Contact.Unknown'))}
                </div>
            </div>

            <Divider />
            <RouteLink to={routes['settings.account.contact']} className="py-4">
                {i18next.t('Settings.Account.Contact')}
            </RouteLink>
            {!autoLoginEnabled && (
                <RouteLink
                    to={routes['settings.account.authentication']}
                    className="py-4"
                >
                    {i18next.t('settings.user.credential.username_password')}
                </RouteLink>
            )}

            {!isBundleCustomer(extensionKey) && (
                <RouteLink
                    to={routes['settings.account.license']}
                    description={getLicenceText()}
                >
                    {i18next.t('Settings.Account.License')}
                </RouteLink>
            )}

            {socialIntegrationSettings &&
            !isTeamsAnyVersion(embedded) &&
            retailerAllowsConnectedServices &&
            (hasGoogleContactOrCalendarCapability ||
                hasMicrosoftContactOrCalendarCapability) ? (
                <>
                    <Divider className="my-3" />

                    <h2 className="pt-4 pb-3 font-bold">
                        {i18next.t('settings.social_integration')}
                    </h2>

                    {hasGoogleContactOrCalendarCapability && (
                        <SocialIntegration
                            integration="google"
                            data={socialIntegrationSettings.get('google')}
                        />
                    )}

                    {hasMicrosoftContactOrCalendarCapability && (
                        <SocialIntegration
                            integration="microsoft"
                            data={socialIntegrationSettings.get('microsoft')}
                        />
                    )}

                    <div className="mt-4 flex flex-col gap-y-5">
                        <h3 className="text-sm font-bold">
                            {i18next.t(
                                'settings.social_integration.contact.about',
                            )}
                        </h3>

                        <AboutListItem
                            icon="checkCircleOutline"
                            title="settings.social_integration.sign_in"
                            description="settings.social_integration.sign_in_info"
                        />

                        <AboutListItem
                            icon="calendar"
                            title="settings.social_integration.synctypes.calendar"
                            description="calendar.other.sync_help"
                        />

                        <AboutListItem
                            icon="person"
                            title="settings.social_integration.synctypes.contacts"
                            description="settings.social_integration.contact_info"
                        />
                    </div>
                </>
            ) : null}
        </Page>
    );
};

export default Account;
